import { FIREBASE_AUTH_ERROR_CODE } from 'shared/constants/firebase/errors';

import type { MaybeAuthLikeError } from './auth';
import {
    isAppleAuthCancellationError,
    isNetworkUnavailableError,
} from './auth';

enum SignUpErrorCode {
    AccountExists,
    AuthCancelled,
    EmailInUse,
    PhoneInUse,
    InvalidEmail,
    NetworkUnavailable,
    Unknown,
}

/**
 * Keys the sign-up endpoints can report. Field names mirror
 * `Users::ValidationSchemas::Create`; `message` is the generic 403/422 body.
 */
type BackendErrorKey =
    | 'message'
    | 'email'
    | 'phone'
    | 'phone_prefix'
    | 'password'
    | 'password_confirmation'
    | 'region'
    | 'first_name'
    | 'last_name'
    | 'access_code';

/**
 * `response.data.errors` as the backend sends it: arrays for field failures,
 * a plain string for `message`.
 *
 * @example
 * { email: ['has already been taken'], phone: ['has already been taken'] }
 * { message: 'Action not allowed' }
 */
type BackendErrors = Partial<Record<BackendErrorKey, string[] | string>>;

/** `BackendErrors` flattened to strings, ready for Formik's `setErrors`. */
type SignUpFieldErrors = Record<string, string>;

type MaybeSignUpError = MaybeAuthLikeError & {
    response?: {
        data?: {
            errors?: BackendErrors;
        };
    };
};

type SignUpErrorResult = {
    code: SignUpErrorCode;
    message: string;
    errors?: SignUpFieldErrors;
};

/**
 * Parses a failed sign-up request into an ErrorCode plus a display message.
 *
 * Merges two signal sources:
 * 1. The client-side Firebase auth error code, set before the request ever
 *    reaches the backend (e.g. network failure or Apple Sign-In cancellation).
 * 2. The backend response body's field keys, which are the only way to tell
 *    "email taken" from "phone taken" from an unrelated error, since the
 *    backend can return the same HTTP status for all three.
 */
function parseSignUpError(error: unknown): SignUpErrorResult {
    const typedError = error as MaybeSignUpError;

    if (isNetworkUnavailableError(typedError)) {
        return {
            code: SignUpErrorCode.NetworkUnavailable,
            message: 'No internet connection',
        };
    }

    if (isAppleAuthCancellationError(typedError)) {
        return {
            code: SignUpErrorCode.AuthCancelled,
            message: 'Apple Sign-In cancelled',
        };
    }

    const errors = typedError?.response?.data?.errors;
    const formattedErrors =
        errors &&
        Object.entries(errors).reduce((acc, [key, value]) => {
            if (value === undefined) {
                return acc;
            }

            acc[key] = Array.isArray(value) ? value.join('\n') : String(value);

            return acc;
        }, {} as SignUpFieldErrors);

    if (typedError?.code === FIREBASE_AUTH_ERROR_CODE.email_already_in_use) {
        return {
            code: SignUpErrorCode.EmailInUse,
            message: 'That email address is already in use!',
            errors: formattedErrors,
        };
    }

    if (typedError?.code === FIREBASE_AUTH_ERROR_CODE.invalid_email) {
        return {
            code: SignUpErrorCode.InvalidEmail,
            message: 'That email address is invalid!',
            errors: formattedErrors,
        };
    }

    if (
        typedError?.code === FIREBASE_AUTH_ERROR_CODE.acount_already_exists ||
        typedError?.message === FIREBASE_AUTH_ERROR_CODE.acount_already_exists
    ) {
        return {
            code: SignUpErrorCode.AccountExists,
            message: 'Account exists with different credential',
            errors: formattedErrors,
        };
    }

    if (errors?.email) {
        return {
            code: SignUpErrorCode.EmailInUse,
            message: 'That email address is already in use!',
            errors: formattedErrors,
        };
    }

    if (errors?.phone || errors?.phone_prefix) {
        return {
            code: SignUpErrorCode.PhoneInUse,
            message: 'That phone number has already been taken!',
            errors: formattedErrors,
        };
    }

    const unknownErrorMessage =
        formattedErrors && formattedErrors.message
            ? formattedErrors.message
            : error
            ? `${error}`
            : '';

    return {
        code: SignUpErrorCode.Unknown,
        message: unknownErrorMessage,
        errors: formattedErrors,
    };
}

export type {
    BackendErrors,
    MaybeSignUpError,
    SignUpErrorResult,
    SignUpFieldErrors,
};

export { SignUpErrorCode, parseSignUpError };
